import { formatCurrency } from '@angular/common';
import { Component, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { TemplateService } from '@app-core/services';
import { OVERLAY_DATA, OverlayRef } from '@app-core/services/overlay';
import { ContractType, PendingContractResponse, PrismicImageData } from '@app-model/api';
import { ButtonComponent } from '@app-shared/components/button/button.component';
import { IconComponent } from '@app-shared/components/icon/icon.component';
import { untilDestroyed } from '@app-shared/utils';
import { selectSmallLogo } from '@app-store/prismic/prismic.selectors';
import * as fromRoot from '@app-store/reducers';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { take } from 'rxjs/operators';

@Component({
  templateUrl: './remit-submitted-popup.component.html',
  styleUrls: ['./remit-submitted-popup.component.scss'],
  imports: [IconComponent, ButtonComponent],
})
export class RemitSubmittedPopupComponent {
  private data = inject<{
    contracts: PendingContractResponse;
    submittedContracts: ContractType[];
  }>(OVERLAY_DATA);
  private _store$ = inject<Store<fromRoot.State>>(Store);
  private _ref = inject<OverlayRef<RemitSubmittedPopupComponent>>(OverlayRef);
  private _templateService = inject(TemplateService);

  errorInContracts: boolean = false;
  displayErrors: { name: string; error: string }[] = [];
  atLeastOneSuccessfulContract: boolean = true;

  private _submittedPendingContracts: ContractType[] = [];
  private _pendingContractResponse: PendingContractResponse;

  readonly #untilDestroyed = untilDestroyed();

  constructor() {
    this._pendingContractResponse = this.data.contracts;
    this._checkForErroredContracts(this.data.contracts, this.data.submittedContracts);
    this._submittedPendingContracts = this.data.submittedContracts;
  }

  printRemit(): void {
    this._store$
      .select(selectSmallLogo)
      .pipe(this.#untilDestroyed())
      .subscribe((navbarLogo: PrismicImageData) => {
        const unsuccessfulContractIDs = this._pendingContractResponse.erroredContracts.declinedErrorContracts
          .map((c) => c.econContractId)
          .concat(this._pendingContractResponse.erroredContracts.remittedErrorContracts.map((c) => c.econContractId));
        const successfulContracts = this._submittedPendingContracts.filter(
          (c) => !unsuccessfulContractIDs.includes(c.econContractId)
        );
        const total = this._getTotalDealerCost(successfulContracts);
        this._templateService.printRemit(successfulContracts, true, navbarLogo?.url || '', total);
      });
  }

  exportClicked(): void {
    this._store$
      .select((store) => store.userState.user)
      .pipe(take(1), this.#untilDestroyed())
      .subscribe((userData) => {
        const unsuccessfulContractIDs = this._pendingContractResponse.erroredContracts.declinedErrorContracts
          .map((c) => c.econContractId)
          .concat(this._pendingContractResponse.erroredContracts.remittedErrorContracts.map((c) => c.econContractId));
        const successfulContracts = this._submittedPendingContracts.filter(
          (c) => !unsuccessfulContractIDs.includes(c.econContractId)
        );
        const headers: { key: string; displayLabel: string }[] = [
          { key: 'dealerNumber', displayLabel: 'DEALER NUMBER' },
          { key: 'customerName', displayLabel: 'CUSTOMER NAME' },
          { key: 'year', displayLabel: 'YEAR' },
          { key: 'model', displayLabel: 'MODEL' },
          { key: 'vin', displayLabel: 'VIN' },
          { key: 'product', displayLabel: 'PRODUCT' },
          { key: 'term', displayLabel: 'TERM' },
          { key: 'contractNumber', displayLabel: 'CONTRACT#' },
          { key: 'dealerCost', displayLabel: 'DEALER COST' },
          { key: 'totalCost', displayLabel: 'TOTAL COST' },
          { key: 'totalDealerCost', displayLabel: 'Total Dealer Cost:' },
        ];
        const data = successfulContracts.map((contract) => ({
          dealerNumber: userData.cmsDealerNumber,
          customerName: contract.firstName ? `${contract.firstName} ${contract.lastName}` : contract.businessName,
          year: contract.year,
          model: contract.model,
          vin: contract.vin,
          product: contract.productCode,
          term: contract.termMonths,
          contractNumber: contract.econContractNumber,
          dealerCost: contract.contractDealerCost,
          totalCost: contract.customerCost,
          totalDealerCost:
            successfulContracts && successfulContracts[0] && contract === successfulContracts[0]
              ? formatCurrency(this._getTotalDealerCost(successfulContracts), 'en-US', '$')
              : '', // leave remaining cells blank
        }));
        this.exportToCSV(data, this.getFileName(successfulContracts), headers);
      });
  }

  close(): void {
    this._ref.close();
  }

  private exportToCSV(data: any, filename: string, columnHeaders: { key: string; displayLabel: string }[]): void {
    const csvConfig = mkConfig({ filename, columnHeaders });

    const csv = generateCsv(csvConfig)(data);

    download(csvConfig)(csv);
  }

  private getFileName(contracts: ContractType[]): string {
    const prependName = 'Remittance Register';
    if (contracts || contracts[0]) {
      return prependName;
    }
    const firstContract = contracts[0];
    const appendDateName =
      firstContract && contracts.every((c) => c.contractGroup2 === firstContract.contractGroup2)
        ? firstContract.contractGroup2
        : firstContract.contractGroup1;

    return prependName + ' ' + appendDateName;
  }

  private _getTotalDealerCost(contracts: ContractType[]): number {
    return contracts.reduce((a, b) => a + b.contractDealerCost, 0);
  }

  private _checkForErroredContracts(
    response: PendingContractResponse,
    submittedContracts: ContractType[] = []
  ): ContractType[] {
    this.errorInContracts = false;
    this.atLeastOneSuccessfulContract = false;
    this.displayErrors = [];

    const contracts = response.erroredContracts;
    if (contracts) {
      if (contracts.declinedErrorContracts && contracts.declinedErrorContracts.length > 0) {
        this.errorInContracts = true;

        this.displayErrors = contracts.declinedErrorContracts.map((data) => {
          return {
            name: `${data.firstName} ${data.lastName} - ${data.description}`,
            error: data.standardResponse.responseDescription,
          };
        });
      }

      if (contracts.remittedErrorContracts && contracts.remittedErrorContracts.length > 0) {
        this.errorInContracts = true;

        this.displayErrors = contracts.remittedErrorContracts.map((data: ContractType) => {
          return {
            name: `${data.firstName} ${data.lastName} - ${data.description}`,
            error: data.standardResponse.responseDescription,
          };
        });
      }
      if (this.displayErrors.length < submittedContracts.length) {
        this.atLeastOneSuccessfulContract = true;
      }

      return submittedContracts.filter((contract: ContractType) => {
        const foundIndx = contracts.remittedErrorContracts.findIndex(
          (x) => x.econContractId === contract.econContractId
        );

        return foundIndx <= -1;
      });
    } else if (this._submittedPendingContracts.length) {
      this.atLeastOneSuccessfulContract = true;
    }

    return submittedContracts;
  }
}
